import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from "react-router-dom";
import {useFormik} from "formik";
import {
    Alert, Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack,
    TextField, Typography,
} from "@mui/material";
import {AltRoute} from "@mui/icons-material";
import BetaBadge from "./layout/BetaBadge";
import Constants from "../../utils/constants";
import history from "../../utils/history";
import {toastHelper} from "../../utils/toast-helper";
import './UsersNewOrEdit.css';
import {
    useCreateSimulationMutation, useEnregistrerSimulationMutation, useGetAgencesSimulationQuery, useGetCalquesCollecteQuery,
    useGetCalquesTourneesQuery, useGetJourneeQuery, useGetSimulationQuery,
} from "../../modules/simulations/simulations.api";
import {useGetPatrimoinesLightByClientQuery} from "../../modules/patrimoines/patrimoines.api";
import {
    appliquerCalendrier, parseFiger, pollingSimulation, valeursInitiales, valeursOuverture,
} from "./simulations/simulationRules";
import {
    affectationsDuPav, afficheeALOuverture, calqueVisible, calquesCollecte, calquesTournees, doitConfirmerSortie, enregistrerScenario,
    etatBoutonJouer, fichePav, figerDansVolet, jouerScenario, libelleProposeEnregistrement, libelleValide, paramsUrl,
    lignesNonAffectees, peutEnregistrer, pointsNonAffectes, raisonDeLaVisite, sectionsEnErreur, verdictSimulation,
} from "./simulations/atelierRules";
import {schemaAtelier} from "./simulations/simulationSchema";
import {useEtatCalendrier} from "./simulations/useEtatCalendrier";
import AtelierVolet from "./simulations/AtelierVolet";
import AtelierCarte from "./simulations/AtelierCarte";
import AtelierLegende from "./simulations/AtelierLegende";
import AtelierRail from "./simulations/AtelierRail";
import VerdictSimulation from "./simulations/VerdictSimulation";
import BoutonExporter from "./simulations/BoutonExporter";
import BadgeImporte from "./simulations/BadgeImporte";
import {rejouerDe, texteSnapshot} from "./simulations/echangeScenarioRules";
import {mentionRecap} from "./simulations/calibrationRules";
import BlocJournee from "./simulations/BlocJournee";
import {CARD_SX} from "./interventions/v2/primitives.js";

const FOND = '#F6F8F4';

/**
 * Atelier sur carte (TOUR-03 lot 4, U-TOUR-03-1) — floorplan Worklist plein écran, classe Bureau.
 * `?patrimoine=&client=&from=&figer=` : `from` pré-remplit le volet depuis \`parametres\` du scénario source et affiche ses calques.
 * Jouer = POST d'un brouillon ; on reste dans l'atelier et on suit le calcul par polling 5 s sur \`GET /simulations/{id}\`.
 */
function SimulationAtelierPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const patrimoineId = searchParams.get('patrimoine');
    const clientId = searchParams.get('client');
    const fromId = searchParams.get('from');
    const figer = useMemo(() => parseFiger(searchParams.get('figer')), [searchParams]);

    const [tentee, setTentee] = useState(false);
    const [erreursGlobales, setErreursGlobales] = useState([]);
    const [ouvertes, setOuvertes] = useState(['periode', 'equipes']);
    const [afficheId, setAfficheId] = useState(afficheeALOuverture(fromId));
    const [choixCalques, setChoixCalques] = useState({});
    const [avecTraces, setAvecTraces] = useState(false);
    const [selection, setSelection] = useState(null);
    const [journeeActive, setJourneeActive] = useState(null);
    const [calibreJoue, setCalibreJoue] = useState(null); // « curseurs calibrés (n journées) » figé au moment de Jouer, avec le scénario affiché
    const [enregistrementOuvert, setEnregistrementOuvert] = useState(false);
    const [libelleEnreg, setLibelleEnreg] = useState('');
    const [erreurEnreg, setErreurEnreg] = useState(null);
    const [sortieOuverte, setSortieOuverte] = useState(false);
    const sortieEnAttente = useRef(null);
    const debloquer = useRef(null);

    const {data: patrimoines = []} = useGetPatrimoinesLightByClientQuery(clientId, {skip: !clientId});
    const patrimoine = patrimoines.find((p) => String(p.id) === String(patrimoineId));
    const {data: agences = [], isLoading: agencesLoading} = useGetAgencesSimulationQuery();
    const {data: source, error: sourceError} = useGetSimulationQuery(fromId, {skip: !fromId});
    const {loading: calendrierLoading, erreur: calendrierErreur, present: aCalendrier} = useEtatCalendrier(patrimoineId);

    const [polling, setPolling] = useState(0);
    // currentData : jamais l'ancien brouillon pendant que le nouveau se charge (Enregistrer, calques).
    const {currentData: affichee} = useGetSimulationQuery(afficheId, {skip: !afficheId, pollingInterval: polling});
    useEffect(() => {
        setPolling(pollingSimulation(affichee));
    }, [affichee]);

    const libellePatrimoine = patrimoine?.libelle ?? source?.patrimoine?.libelle ?? affichee?.patrimoine?.libelle ?? null;

    // Calques : collecte (sans calcul) dès l'ouverture ; tournées du scénario affiché quand il est `done` (409 sinon).
    const {data: donneesCollecte} = useGetCalquesCollecteQuery(patrimoineId, {skip: !patrimoineId});
    const {currentData: donneesTournees} = useGetCalquesTourneesQuery(afficheId, {skip: !afficheId || affichee?.statut !== 'done'});
    const calquesC = useMemo(() => calquesCollecte(donneesCollecte), [donneesCollecte]);
    const calquesT = useMemo(() => calquesTournees(donneesTournees), [donneesTournees]);
    const nonAffectes = useMemo(() => pointsNonAffectes(donneesTournees), [donneesTournees]);
    const aTournees = calquesT.length > 0;
    const visible = useCallback((cle) => calqueVisible(cle, choixCalques, {aTournees}), [choixCalques, aTournees]);
    const basculer = (cle) => setChoixCalques((c) => ({...c, [cle]: !calqueVisible(cle, c, {aTournees})}));
    const selectionnerPav = useCallback((nomenclature, journee) => {
        setSelection(nomenclature);
        if (journee) setJourneeActive(journee);
    }, []);

    const [createSimulation, {isLoading: envoi}] = useCreateSimulationMutation();
    const [enregistrer, {isLoading: enregistrement}] = useEnregistrerSimulationMutation();

    const formik = useFormik({
        initialValues: valeursInitiales(),
        validationSchema: schemaAtelier,
        validateOnMount: false,
        onSubmit: async (values) => {
            setErreursGlobales([]);
            try {
                const r = await jouerScenario((payload) => createSimulation(payload).unwrap(), values, patrimoineId, new Date(), rejouerDe(source));
                if (r.cree) {
                    setAfficheId(String(r.cree.id));
                    setCalibreJoue(mentionRecap(values));
                    setSelection(null);
                    setJourneeActive(null);
                    setChoixCalques({});
                    return;
                }
                formik.setErrors(r.erreurs);
                setErreursGlobales(r.globales);
                setOuvertes((o) => [...new Set([...o, ...sectionsEnErreur(r.erreurs)])]);
            } catch (e) {
                toastHelper.handleError(e, "Oops, une erreur est survenue.");
            }
        },
    });

    // Pré-remplissage : `?from=<id>` (Dupliquer / Figer ce jour), sinon vierge ; le calendrier fixe le défaut de « contraint ».
    useEffect(() => {
        if ((fromId && !source && !sourceError) || calendrierLoading) return;
        const valeurs = valeursOuverture(fromId && source ? source : null, figer);
        const base = fromId && source ? valeurs : {...valeurs, contraint: aCalendrier};
        formik.resetForm({values: appliquerCalendrier(base, aCalendrier)});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, sourceError, fromId, figer, calendrierLoading, aCalendrier]);

    // Rail de contexte : fiche du PAV cliqué ; la raison d'une visite vient du détail de sa journée.
    const affectationsSel = useMemo(() => affectationsDuPav(calquesT, selection), [calquesT, selection]);
    const dateSel = affectationsSel[0]?.date ?? null;
    const {data: detailJournee} = useGetJourneeQuery({id: afficheId, date: dateSel}, {skip: !selection || !dateSel || !afficheId});
    const fiche = useMemo(() => fichePav({
        nomenclature: selection, collecte: donneesCollecte, calques: calquesT, nonAffectees: donneesTournees?.nonAffectees,
        raisonVisite: raisonDeLaVisite(detailJournee, selection, affectationsSel[0]?.ordre),
    }), [selection, donneesCollecte, calquesT, donneesTournees, detailJournee, affectationsSel]);
    const positionSel = useMemo(() => {
        const p = (donneesCollecte?.pavs || []).find((x) => x.nomenclature === selection);
        if (p) return [p.lat, p.lon];
        return calquesT.flatMap((c) => c.points).find((x) => x.nomenclature === selection)?.position ?? null;
    }, [donneesCollecte, selection, calquesT]);

    const figerCeJour = (nomenclature, date) => {
        formik.setValues(figerDansVolet(formik.values, {nomenclature, date}));
        setOuvertes((o) => [...new Set([...o, 'verrous'])]);
        toastHelper.displaySuccess(`${nomenclature} figé le ${date} : rejouez pour en tenir compte.`);
    };

    const verdict = useMemo(() => verdictSimulation(affichee), [affichee]);
    const echec = affichee?.statut === 'failed';
    const ouvrirEnregistrement = () => {
        setLibelleEnreg(libelleProposeEnregistrement(formik.values, affichee));
        setErreurEnreg(null);
        setEnregistrementOuvert(true);
    };
    const confirmerEnregistrement = async () => {
        if (!libelleValide(libelleEnreg)) {
            setErreurEnreg('Libellé : 1 à 120 caractères.');
            return;
        }
        try {
            await enregistrerScenario((arg) => enregistrer(arg).unwrap(), affichee.id, libelleEnreg);
            toastHelper.displaySuccess("Scénario enregistré : il rejoint la table de comparaison.");
            setEnregistrementOuvert(false);
        } catch (e) {
            if (e?.status === 422) setErreurEnreg(e.data?.violations?.[0]?.message ?? 'Libellé invalide.');
            else if (e?.status === 409) {
                setEnregistrementOuvert(false);
                toastHelper.handleError(e, "Le calcul n'est pas terminé.");
            } else toastHelper.handleError(e, "Oops, une erreur est survenue.");
        }
    };

    const confirmerSortie = doitConfirmerSortie(affichee);
    // Toute navigation (menu, retour du navigateur, bouton Retour) est suspendue tant qu'un brouillon est à perdre ;
    // history v5 pose aussi son propre beforeunload pour la fermeture de l'onglet.
    useEffect(() => {
        if (!confirmerSortie) return undefined;
        const lever = history.block((tx) => {
            sortieEnAttente.current = tx;
            setSortieOuverte(true);
        });
        debloquer.current = lever;
        return () => {
            lever();
            debloquer.current = null;
        };
    }, [confirmerSortie]);
    const quitter = () => {
        const tx = sortieEnAttente.current;
        sortieEnAttente.current = null;
        debloquer.current?.();
        setSortieOuverte(false);
        if (tx) tx.retry(); else navigate(retour);
    };

    const sectionsFautives = useMemo(() => sectionsEnErreur(formik.errors), [formik.errors]);

    const toggleSection = (id) => setOuvertes((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

    const jouer = async () => {
        setTentee(true);
        const errs = await formik.validateForm();
        if (Object.keys(errs).length > 0) {
            setOuvertes((o) => [...new Set([...o, ...sectionsEnErreur(errs)])]);
            return;
        }
        formik.handleSubmit();
    };

    const bouton = etatBoutonJouer({envoi, simulation: affichee, peut: true});
    const retour = Constants.APP_ROUTE_SIMULATIONS + paramsUrl({client: clientId, patrimoine: patrimoineId});

    if (!patrimoineId) {
        return (
            <Stack spacing={2}>
                <Alert severity="info">Choisissez d'abord un client et un patrimoine depuis la liste des simulations.</Alert>
                <div><Button color="primary" onClick={() => navigate(Constants.APP_ROUTE_SIMULATIONS)}>Retour aux simulations</Button></div>
            </Stack>
        );
    }

    const pied = (
        <Button fullWidth variant="contained" onClick={jouer} disabled={bouton.disabled}
                startIcon={bouton.calcul ? <CircularProgress size={18} color="inherit"/> : null}
                sx={{
                    borderRadius: 999, py: 1.1, fontWeight: 700, textTransform: 'none', fontSize: 15,
                    bgcolor: '#213067', color: '#fff', '&:hover': {bgcolor: '#0F2D69'},
                    '&.Mui-disabled': {bgcolor: '#E3E7EC', color: '#8A929C'},
                }}>
            {bouton.label}
        </Button>
    );

    return (
        <Box sx={{bgcolor: echec ? '#FBF4F4' : FOND, m: '-1rem', p: 2, height: 'calc(100vh - var(--topbar-height) - .5rem)', boxSizing: 'border-box',
            display: 'flex', flexDirection: 'column', gap: 2}} data-testid="atelier">
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{flex: 'none'}}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{minWidth: 0}}>
                    <AltRoute sx={{color: '#213067'}}/>
                    <Typography component="h1" sx={{fontSize: 20, fontWeight: 800, color: '#213067'}} noWrap>
                        Atelier de découpage{libellePatrimoine ? ` · ${libellePatrimoine}` : ''}
                    </Typography>
                    <BetaBadge/>
                    <BadgeImporte simulation={affichee ?? source}/>
                </Stack>
                <Button color="primary" onClick={() => navigate(retour)}>Retour aux scénarios</Button>
            </Stack>

            {fromId && source && texteSnapshot(source) &&
                <Typography variant="body2" data-testid="snapshot" sx={{flex: 'none', color: '#213067', fontWeight: 600}}>{texteSnapshot(source)}</Typography>}

            {fromId && sourceError &&
                <Alert severity="error" sx={{flex: 'none'}}>Scénario source introuvable : le volet est vierge.</Alert>}

            <Box sx={{flex: '1 1 auto', minHeight: 0, display: 'grid', gridTemplateColumns: {xs: '1fr', md: '360px minmax(0, 1fr)'}, gap: 2}}>
                <AtelierVolet formik={formik} tentee={tentee} patrimoine={patrimoineId} agences={agences} agencesLoading={agencesLoading}
                              aCalendrier={aCalendrier} calendrierLoading={calendrierLoading} calendrierErreur={calendrierErreur}
                              ouvertes={ouvertes} onToggle={toggleSection} sectionsFautives={sectionsFautives}
                              erreursGlobales={erreursGlobales} pied={pied}/>
                <Box sx={{minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2}} data-testid="atelier-droite">
                    <VerdictSimulation verdict={verdict}
                        sousLigne={verdict && affichee ? [affichee.libelle, affichee.enregistre ? 'enregistré' : 'brouillon',
                            verdict.nonAffectees > 0 ? `${verdict.nonAffectees} visites non affectées` : null,
                            calibreJoue].filter(Boolean).join(' · ') : null}
                        details={affichee?.statut === 'done' ? lignesNonAffectees(affichee) : []}
                        actions={affichee?.statut === 'done' && (<>
                            <BoutonExporter simulation={affichee}/>
                            {affichee.enregistre
                                ? <Typography sx={{fontWeight: 700, color: '#5C8316'}}>Enregistré</Typography>
                                : <Button variant="contained" disabled={!peutEnregistrer(affichee)} onClick={ouvrirEnregistrement}
                                          sx={{borderRadius: 999, textTransform: 'none', fontWeight: 700, bgcolor: '#87BC26', color: '#fff', '&:hover': {bgcolor: '#5C8316'}}}>
                                    Enregistrer
                                </Button>}</>)}/>
                    <Collapse in={Boolean(journeeActive && affichee?.statut === 'done')} unmountOnExit sx={{flex: 'none'}}>
                        {journeeActive && affichee &&
                            <Box sx={{...CARD_SX, maxHeight: 280, overflowY: 'auto', p: 1}}>
                                <BlocJournee simulation={affichee} date={journeeActive.split('|')[0]} avecCarte={false} onFiger={figerCeJour}/>
                            </Box>}
                    </Collapse>
                    <Box sx={{flex: '1 1 auto', minHeight: 0, display: 'flex', flexWrap: {xs: 'wrap', lg: 'nowrap'}, gap: 2}}>
                        <Box sx={{...CARD_SX, position: 'relative', flex: '1 1 0', minWidth: 0, minHeight: 320}} data-testid="carte-atelier">
                            <AtelierCarte pavs={donneesCollecte?.pavs} calquesCollecte={calquesC} calquesTournees={calquesT}
                                          nonAffectes={nonAffectes} visible={visible} avecTraces={avecTraces}
                                          selection={selection} journeeActive={journeeActive}
                                          onSelectPav={selectionnerPav}
                                          cadrageCle={`${patrimoineId}|${aTournees ? afficheId : ''}`}/>
                            <AtelierLegende nbPav={donneesCollecte?.pavs?.length ?? 0} calquesCollecte={calquesC} calquesTournees={calquesT}
                                            nbNonAffectes={donneesTournees?.nonAffectees?.length ?? 0}
                                            visible={visible} onToggle={basculer} avecTraces={avecTraces}
                                            onToggleTraces={() => setAvecTraces((t) => !t)}
                                            journeeActive={journeeActive} onSelectJournee={(cle) => setJourneeActive((a) => (a === cle ? null : cle))}/>
                        </Box>
                        {fiche && <AtelierRail fiche={fiche} position={positionSel} onFermer={() => setSelection(null)} onFiger={figerCeJour}/>}
                    </Box>
                </Box>
            </Box>

            <Dialog open={enregistrementOuvert} onClose={() => setEnregistrementOuvert(false)} fullWidth maxWidth="xs">
                <DialogTitle>Enregistrer le scénario</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth size="small" label="Libellé" value={libelleEnreg} sx={{mt: 1}}
                               onChange={(e) => setLibelleEnreg(e.target.value)} error={Boolean(erreurEnreg)} helperText={erreurEnreg}/>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setEnregistrementOuvert(false)}>Annuler</Button>
                    <Button color="secondary" variant="contained" sx={{color: 'white'}} disabled={enregistrement} onClick={confirmerEnregistrement}>Enregistrer</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={sortieOuverte} onClose={() => { sortieEnAttente.current = null; setSortieOuverte(false); }}>
                <DialogTitle>Quitter l'atelier ?</DialogTitle>
                <DialogContent><DialogContentText>Ce brouillon n'est pas enregistré : il sera purgé.</DialogContentText></DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => { sortieEnAttente.current = null; setSortieOuverte(false); }}>Rester</Button>
                    <Button color="secondary" variant="contained" sx={{color: 'white'}} onClick={quitter}>Quitter</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SimulationAtelierPage;
